import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { DocumentPanel } from '../../components/panels/DocumentPanel';
import { ApiError, documentsApi, foldersApi } from '../../lib/api';
import type { DocumentRow, Folder } from '../../lib/types';
import { SkeletonPage } from '../../components/ui/Skeleton';

// pdf.js is heavy; the Documents screen loads it the same way.
const DocPreview = lazy(async () => ({
  default: (await import('../DocPreview')).DocPreview,
}));

interface Props {
  wellId: number;
  /** Which of the well's folders to read, by name. */
  folder: RegExp;
  /** The panel's label while nothing is open. */
  title: string;
  /** Shown when the well has no such folder, or the folder is empty. */
  fallback: ReactNode;
}

function findFolder(folders: Folder[], name: RegExp): Folder | null {
  for (const f of folders) {
    if (f.kind === 'folder' && name.test(f.name)) return f;
    const inner = findFolder(f.children ?? [], name);
    if (inner) return inner;
  }
  return null;
}

/**
 * The newest document of one folder, opened in the real viewer. Previous
 * steps back to older reports, Next forward to newer ones. The API lists a
 * folder newest first, so position 0 is the latest.
 */
export function LatestDocPanel({ wellId, folder, title, fallback }: Props) {
  // undefined while the tree loads, null when the well has no such folder.
  const [folderId, setFolderId] = useState<number | null | undefined>(undefined);
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState<{ doc: DocumentRow | null; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    foldersApi
      .tree(wellId)
      .then((res) => {
        if (!cancelled) setFolderId(findFolder(res.items, folder)?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setFolderId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [wellId, folder]);

  useEffect(() => {
    if (!folderId) return;
    let cancelled = false;
    documentsApi
      .list(folderId, index + 1, 1)
      .then((res) => {
        if (cancelled) return;
        setShown({ doc: res.items[0] ?? null, total: res.total });
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load the document.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [folderId, index]);

  const panel = (file: string) => ({ title, file, page: 1, pageCount: 1, kind: 'geology' as const });

  if (folderId === null || shown?.total === 0) {
    return <DocumentPanel doc={panel(title)}>{fallback}</DocumentPanel>;
  }

  const doc = shown?.doc ?? null;
  const total = shown?.total ?? 0;
  const older = index + 1 < total ? () => setIndex(index + 1) : undefined;
  const newer = index > 0 ? () => setIndex(index - 1) : undefined;

  return (
    <DocumentPanel
      doc={panel(doc?.name ?? title)}
      onPrev={older}
      onNext={newer}
      viewer
    >
      {error ? (
        <p className="pdfnotice">{error}</p>
      ) : doc ? (
        <Suspense fallback={<SkeletonPage />}>
          <DocPreview key={doc.id} doc={doc} thumbnails={false} />
        </Suspense>
      ) : (
        <SkeletonPage />
      )}
    </DocumentPanel>
  );
}
